import { memo, useEffect, useCallback, type ReactNode } from 'react';
import Plus from 'assets/icons/plus.svg?react';
import Minus from 'assets/icons/minus.svg?react';
import { areEqualFields } from 'utils/form';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { isFiniteNumber } from './numbers';
import type { TFieldComponentProps, TFormValues } from './types';
import s from './Counter.module.scss';

type TCounterCondition = 'increment' | 'decrement';

export interface ICounterProps extends TFieldComponentProps<number | null | undefined> {
  label?: ReactNode;
  defaultValue?: number;
  min?: number;
  max?: number;
  description?: ReactNode;
  largeLabel?: boolean;
  callbackOnChange?: (
    value: number | null | undefined,
    values: TFormValues,
    setFieldValue: TFieldComponentProps<number>['form']['setFieldValue'],
  ) => void;
}

// The comparisons and `+ 1`/`- 1` coerce a missing value like the former code did (null to 0,
// undefined to NaN), so Number() keeps them as they were.
const Counter = ({
  field: { name, value },
  form: { setFieldValue, touched, errors, values },
  label,
  defaultValue,
  min = 0,
  max,
  description,
  largeLabel = false,
  callbackOnChange,
}: ICounterProps) => {
  const { formatMessage: t } = useIntl();

  const handleOnChangeNumber = useCallback(
    (condition: TCounterCondition) => {
      let newValue = value;
      if (condition === 'increment' && Number(newValue) < Number(max)) {
        newValue = Number(newValue) + 1;
      }
      if (condition === 'decrement' && Number(newValue) > min) newValue = Number(newValue) - 1;
      if (Number(value) < min) newValue = min;
      setFieldValue(name, newValue);
      if (typeof callbackOnChange === 'function') callbackOnChange(newValue, values, setFieldValue);
    },
    [value, max, min, setFieldValue, name, callbackOnChange, values],
  );

  useEffect(() => {
    if (!Number.isFinite(value)) {
      setFieldValue(name, defaultValue || min);
    }
    if (isFiniteNumber(value) && value < min) setFieldValue(name, min);
  }, [name, value, defaultValue, setFieldValue, min]);

  return (
    <div className={cn('field-wrapper', largeLabel ? 'field-with-large-label' : '')}>
      <div className={s.counter}>
        <button
          onClick={() => handleOnChangeNumber('decrement')}
          type="button"
          className={cn(s.counterBtn, s.ripple)}
          aria-label="Minus-"
        >
          <Minus title={t({ id: 'alt_text.icons.minus' })} />
        </button>
        <span className={s.value}>{value}</span>
        <button
          onClick={() => handleOnChangeNumber('increment')}
          type="button"
          className={cn(s.counterBtn, s.ripple)}
          aria-label="Plus"
        >
          <Plus title={t({ id: 'alt_text.icons.plus' })} />
        </button>
      </div>
      <label htmlFor={name} className="label-when-focused-input">
        {label}
      </label>
      {description ? <div className={s.description}>{description}</div> : null}
      {touched[name] && errors[name] && <div className="error-text">{errors[name]}</div>}
    </div>
  );
};

export default memo(Counter, areEqualFields);
